import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { BsPlusLg } from "react-icons/bs";
import { Skeleton } from "../../Components/Needed";
import { EmptyState, StatusChip } from "../../Components/Kit";
import { listCampaigns } from "../../Helpers/campaignApi";
import { CHANNEL_SHORT, STATUS_LABEL, STATUS_TONE, rupees, scheduleText, whenText } from "./shared";
import "../../Assets/css/notification-centre.css";
import "./campaigns.css";

const FILTERS = ["", "draft", "scheduled", "running", "paused", "completed", "cancelled"];
const n = (value) => Number(value || 0).toLocaleString("en-IN");

// Campaigns list (Phase 8.9, cards since R8.1): status, who it goes to, when it runs, and its reach and
// results so far (people reached, messages, bookings the run reports counted).
const CampaignList = () => {
    const navigate = useNavigate();
    const [status, setStatus] = useState("");
    const [rows, setRows] = useState(null);

    const load = useCallback(async () => {
        setRows(null);
        const result = await listCampaigns(status ? { status } : {});
        if (!result.ok) { toast.error(result.message); setRows([]); return; }
        setRows(result.data.campaigns || []);
    }, [status]);
    useEffect(() => { load(); }, [load]);

    const open = (c) => navigate(`/campaigns/${c.campaignId}`);

    return (
        <div className="settings-page">
            <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
                <div>
                    <h4 className="fw-semibold mb-1">Campaigns</h4>
                    <p className="text-muted mb-0">Messages to many customers or staff at once: offers, news and reminders, by push, WhatsApp, SMS and email.</p>
                </div>
                <button type="button" className="btn btn-primary d-inline-flex align-items-center gap-2" onClick={() => navigate("/campaigns/new")}>
                    <BsPlusLg aria-hidden="true" /> New campaign
                </button>
            </div>

            <div className="nc-pills mb-3" role="group" aria-label="Filter by status">
                {FILTERS.map((key) => (
                    <button key={key || "all"} type="button" aria-pressed={status === key} className={`nc-pill ${status === key ? "is-active" : ""}`} onClick={() => setStatus(key)}>
                        {key ? STATUS_LABEL[key] : "All"}
                    </button>
                ))}
            </div>

            {!rows ? (
                <div className="cl-cards">{[0, 1, 2].map((i) => <Skeleton key={i} height={210} />)}</div>
            ) : rows.length === 0 ? (
                <EmptyState
                    title={status ? `No campaigns that are ${STATUS_LABEL[status].toLowerCase()}` : "No campaigns yet"}
                    text={status ? "" : "Send an offer, a reminder or news to a group of customers or staff."}
                    action={status ? null : <button type="button" className="btn btn-primary btn-sm" onClick={() => navigate("/campaigns/new")}>New campaign</button>}
                />
            ) : (
                <div className="cl-cards">
                    {rows.map((c) => {
                        const r = c.results;
                        return (
                            <article key={c.campaignId} className="cl-card">
                                <div className="cl-card-top">
                                    <StatusChip tone={STATUS_TONE[c.status] || "neutral"}>{STATUS_LABEL[c.status] || c.status}</StatusChip>
                                    <span className="cl-sub">{c.audienceType === "staff" ? "Staff" : "Customers"} · {c.purpose === "service" ? "Service" : "Promotional"}{c.shadow ? " · shadow" : ""}</span>
                                </div>
                                <button type="button" className="cl-card-name" onClick={() => open(c)}>{c.name}</button>
                                <div className="cl-chips">
                                    {(c.channels || []).map((ch) => <span key={ch} className="cl-chip">{CHANNEL_SHORT[ch]}</span>)}
                                    {c.coupon?.couponCode ? <span className="cl-chip is-coupon">Coupon {c.coupon.couponCode}</span> : null}
                                </div>
                                <div className="cl-sub">{scheduleText(c.schedule)}{c.nextRunAt ? ` · next ${whenText(c.nextRunAt)}` : ""}</div>
                                <dl className="cl-stats">
                                    <div><dt>Reached</dt><dd>{r ? n(r.reached) : "-"}</dd></div>
                                    <div><dt>Messages</dt><dd>{r ? n(r.messages) : "-"}</dd></div>
                                    <div><dt>Bookings</dt><dd>{r?.counted ? n(r.confirmed) : "-"}</dd></div>
                                    <div><dt>Revenue</dt><dd>{r?.counted ? rupees(r.revenue) : "-"}</dd></div>
                                </dl>
                                <div className="cl-card-foot">
                                    <span className="cl-sub">{c.lastRunAt ? `Last run ${whenText(c.lastRunAt)} · ${c.runCount || 0} run${c.runCount === 1 ? "" : "s"}` : "Not run yet"}{r && !r.counted && r.runs ? " · open the report to count bookings" : ""}</span>
                                    <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => open(c)}>Open</button>
                                </div>
                            </article>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default CampaignList;
